import { createORPCClient, onError } from "@orpc/client";
import { RPCLink } from "@orpc/client/fetch";
import type { RouterClient } from "@orpc/server";
import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import type { RouterUtils } from "@orpc/tanstack-query";
import type { AppRouter } from "@papier/api";
import { env } from "@papier/env/env-frontend";
import { generateRequestId } from "./request-id";

function getOrpcOrigin() {
  const serverHost = env.VITE_SERVERSIDE_HOST;
  if (serverHost && import.meta.hot) {
    // Dev: keep the page hostname (LAN/tunnel testing) but use the backend port.
    const serverUrl = new URL(serverHost);
    serverUrl.hostname = window.location.hostname;
    return serverUrl.origin;
  }
  // Production has no same-origin /api proxy (static hosting answers 405),
  // so route oRPC to the backend host. Backend CORS allows *.usepapier.io
  // with credentials.
  if (serverHost) return new URL(serverHost).origin;
  return window.location.origin;
}

const link = new RPCLink({
  url: new URL("/api/rpc", getOrpcOrigin()).toString(),
  headers: () => ({
    "Content-Type": "application/json",
    "x-request-id": generateRequestId(),
  }),
  fetch(request, init = {}) {
    return globalThis.fetch(request, { ...init, credentials: "include" });
  },
  interceptors: [
    onError((error) => {
      if (error instanceof Error && error.name !== "AbortError") console.error(error);
    }),
  ],
});

type AppRouterClient = RouterClient<AppRouter>;

export const orpcClient: AppRouterClient = createORPCClient<AppRouterClient>(link);
export const orpc: RouterUtils<AppRouterClient> = createTanstackQueryUtils(orpcClient);
